import {
  Banknote,
  CalendarPlus,
  FileText,
  Mail,
  MessageSquare,
  Receipt,
  Send,
  Shield,
  ShoppingCart,
  UserPlus,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { CreateManualActionInput } from "./hooks/useCreateManualAction";
import type { WorkspaceTab } from "../workspace-router/state/workspaceStore";

/**
 * Manual action catalog — the SINGLE source of truth for "Nuova azione".
 *
 * Every action the user can start BY HAND from the working area (the
 * counterpart to an AI proposal) is declared once here, with the
 * ``CreateManualActionInput`` ready to hand to ``useCreateManualAction``.
 * Both the global Cmd+K palette ("Nuova azione" group) and the per-area
 * shortcuts (Finanza → "Nuova fattura", CRM → "Nuovo cliente", …) render
 * from this list — DRY, so adding an action surfaces it everywhere at once.
 *
 * ``area`` lets per-area surfaces filter to their own actions
 * (``manualActionsForArea("finance")``); the palette shows them all.
 *
 * The BE wires manual creation per ``action_type`` (+ ``specialized_action_type``
 * / ``sobject_type``) — see ``ando-agent/.../action_service.py::create_manual_proposal``.
 * Action types not yet BE-supported simply aren't listed here.
 */

export interface ManualActionDef {
  /** Stable id for the palette row / list key. */
  readonly id: string;
  /** Menu label, e.g. "Nuova fattura". */
  readonly label: string;
  /** Working-area area this action belongs to (for per-area grouping). */
  readonly area: WorkspaceTab;
  /** Lucide icon component for the row. */
  readonly icon: LucideIcon;
  /** Fuzzy-search keywords. */
  readonly keywords?: readonly string[];
  /** Everything needed to mint the proposal + name its working-area surface. */
  readonly input: CreateManualActionInput;
}

export const MANUAL_ACTION_CATALOG: readonly ManualActionDef[] = [
  // ── Finanza ──────────────────────────────────────────────────────────────
  {
    id: "manual:fic_create_invoice",
    label: "Nuova fattura",
    area: "finance",
    icon: Receipt,
    keywords: ["fattura", "invoice", "fatture in cloud", "vendita"],
    input: {
      action_type: "fatture_in_cloud_action",
      provider: "fatture_in_cloud",
      specialized_action_type: "create_invoice",
      title: "Nuova fattura",
      vendorLabel: "Fatture in Cloud",
    },
  },
  {
    id: "manual:fic_payment",
    label: "Registra incasso",
    area: "finance",
    icon: Wallet,
    keywords: ["incasso", "pagamento", "saldo", "payment", "registra"],
    input: {
      action_type: "fatture_in_cloud_action",
      provider: "fatture_in_cloud",
      specialized_action_type: "mark_invoice_paid",
      title: "Registra incasso fattura",
      vendorLabel: "Fatture in Cloud",
    },
  },
  {
    id: "manual:fic_send_to_sdi",
    label: "Invia a SdI",
    area: "finance",
    icon: Send,
    keywords: ["sdi", "fattura elettronica", "invia", "sistema di interscambio"],
    input: {
      action_type: "fatture_in_cloud_action",
      provider: "fatture_in_cloud",
      specialized_action_type: "send_to_sdi",
      title: "Invia fattura a SdI",
      vendorLabel: "Fatture in Cloud",
    },
  },
  {
    id: "manual:fic_credit_note",
    label: "Nota di credito",
    area: "finance",
    icon: Receipt,
    keywords: ["nota di credito", "credit note", "storno", "rimborso"],
    input: {
      action_type: "fatture_in_cloud_action",
      provider: "fatture_in_cloud",
      specialized_action_type: "create_credit_note",
      title: "Nuova nota di credito",
      vendorLabel: "Fatture in Cloud",
    },
  },
  {
    id: "manual:fic_quote",
    label: "Preventivo",
    area: "finance",
    icon: FileText,
    keywords: ["preventivo", "quote", "proforma", "offerta"],
    input: {
      action_type: "fatture_in_cloud_action",
      provider: "fatture_in_cloud",
      specialized_action_type: "create_quote_or_proforma",
      title: "Nuovo preventivo",
      vendorLabel: "Fatture in Cloud",
    },
  },
  {
    id: "manual:fic_delivery_note",
    label: "DDT",
    area: "finance",
    icon: FileText,
    keywords: ["ddt", "documento di trasporto", "delivery note", "consegna"],
    input: {
      action_type: "fatture_in_cloud_action",
      provider: "fatture_in_cloud",
      specialized_action_type: "create_delivery_note",
      title: "Nuovo DDT",
      vendorLabel: "Fatture in Cloud",
    },
  },
  {
    id: "manual:sepa_payment",
    label: "Disponi bonifico SEPA",
    area: "finance",
    icon: Banknote,
    keywords: ["bonifico", "sepa", "pagamento", "open banking", "payment"],
    input: {
      action_type: "initiate_sepa_payment",
      title: "Disponi bonifico SEPA",
      vendorLabel: "Open Banking",
    },
  },
  // ── Calendario ─────────────────────────────────────────────────────────
  {
    id: "manual:calendar_create_event",
    label: "Nuovo evento",
    area: "calendar",
    icon: CalendarPlus,
    keywords: ["evento", "appuntamento", "riunione", "meeting", "calendario"],
    input: {
      action_type: "create_event",
      title: "Nuovo evento",
      vendorLabel: "Calendario",
    },
  },
  // ── Conversazioni ─────────────────────────────────────────────────────────
  {
    id: "manual:send_email",
    label: "Nuova email",
    area: "conversations",
    icon: Mail,
    keywords: ["email", "mail", "messaggio", "scrivi"],
    input: {
      action_type: "send_email",
      title: "Nuova email",
      vendorLabel: "Email",
    },
  },
  {
    id: "manual:send_message",
    label: "Nuovo messaggio",
    area: "conversations",
    icon: MessageSquare,
    keywords: ["messaggio", "slack", "teams", "chat"],
    input: {
      action_type: "send_message",
      title: "Nuovo messaggio",
      vendorLabel: "Messaggi",
    },
  },
  {
    id: "manual:aruba_pec_send",
    label: "Invia PEC",
    area: "conversations",
    icon: Shield,
    keywords: ["pec", "posta certificata", "aruba", "raccomandata"],
    input: {
      action_type: "aruba_pec_send",
      title: "Invia PEC",
      vendorLabel: "PEC",
    },
  },
  // ── CRM (Sales) ───────────────────────────────────────────────────────────
  // Native Ando CRM (Jul 2026) — provider "ando_crm" writes the crm_* spine
  // through the same generic create_record HITL pipeline (no external vendor).
  {
    id: "manual:crm_create_deal",
    label: "Nuova opportunità",
    area: "sales",
    icon: UserPlus,
    keywords: ["opportunità", "deal", "pipeline", "trattativa", "crm", "vendita"],
    input: {
      action_type: "create_record",
      provider: "ando_crm",
      sobject_type: "Deal",
      title: "Nuova opportunità",
      vendorLabel: "CRM",
    },
  },
  {
    id: "manual:crm_create_contact",
    label: "Nuovo contatto CRM",
    area: "sales",
    icon: UserPlus,
    keywords: ["contatto", "persona", "crm", "anagrafica", "cliente"],
    input: {
      action_type: "create_record",
      provider: "ando_crm",
      sobject_type: "Contact",
      title: "Nuovo contatto",
      vendorLabel: "CRM",
    },
  },
  {
    id: "manual:crm_create_company",
    label: "Nuova azienda CRM",
    area: "sales",
    icon: UserPlus,
    keywords: ["azienda", "company", "account", "crm", "cliente"],
    input: {
      action_type: "create_record",
      provider: "ando_crm",
      sobject_type: "Company",
      title: "Nuova azienda",
      vendorLabel: "CRM",
    },
  },
  {
    id: "manual:salesforce_create_lead",
    label: "Nuovo lead",
    area: "sales",
    icon: UserPlus,
    keywords: ["lead", "crm", "salesforce", "contatto"],
    input: {
      action_type: "create_record",
      provider: "salesforce",
      sobject_type: "Lead",
      title: "Nuovo lead",
      vendorLabel: "Salesforce",
    },
  },
  {
    id: "manual:salesforce_create_contact",
    label: "Nuovo cliente / contatto",
    area: "sales",
    icon: UserPlus,
    keywords: ["cliente", "contatto", "contact", "crm", "salesforce", "anagrafica"],
    input: {
      action_type: "create_record",
      provider: "salesforce",
      sobject_type: "Contact",
      title: "Nuovo contatto",
      vendorLabel: "Salesforce",
    },
  },
  // NB: HubSpot ``create_record`` manual actions were removed (Jun 2026) — the
  // BE ``create_manual_proposal`` only accepts salesforce / fatture_in_cloud,
  // so they always 422'd. Re-add once the BE supports the HubSpot write path.
  // ── ERP (Amministrazione) ──────────────────────────────────────────
  // Jun 12, 2026: SAP B1 moved from "work" to "finance" — Orders &
  // BusinessPartners are administrative ERP operations, not generic
  // work-area shortcuts. The user explicitly asked for SAP entries to
  // live next to Nuova fattura / Registra incasso / Invia a SdI.
  {
    id: "manual:sap_create_order",
    label: "SAP — Nuovo ordine",
    area: "finance",
    icon: ShoppingCart,
    keywords: ["ordine", "order", "sap", "erp", "vendita"],
    input: {
      action_type: "create_record",
      provider: "sap_b1",
      sobject_type: "Orders",
      title: "Nuovo ordine (SAP)",
      vendorLabel: "SAP B1",
    },
  },
  {
    id: "manual:sap_create_business_partner",
    label: "SAP — Registra cliente",
    area: "finance",
    icon: UserPlus,
    keywords: ["cliente", "fornitore", "business partner", "sap", "erp", "anagrafica"],
    input: {
      action_type: "create_record",
      provider: "sap_b1",
      sobject_type: "BusinessPartners",
      title: "Registra cliente (SAP)",
      vendorLabel: "SAP B1",
    },
  },
];

/** Manual actions for one working-area tab (per-area shortcuts). */
export function manualActionsForArea(
  area: WorkspaceTab,
): readonly ManualActionDef[] {
  return MANUAL_ACTION_CATALOG.filter((a) => a.area === area);
}
